import { ref, watch, onMounted } from 'vue';

const isDark = ref(false);

// Initialize theme state immediately on module load
if (typeof window !== 'undefined') {
  const savedTheme = localStorage.getItem('theme');
  if (savedTheme) {
    isDark.value = savedTheme === 'dark';
  } else {
    isDark.value = window.matchMedia('(prefers-color-scheme: dark)').matches;
  }
  if (isDark.value) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

/**
 * Reusable Theme Transition Hook
 * Provides a super-smooth, glowing liquid water ripple animation when toggling dark/light mode
 * using Document View Transitions API with CSS fallback.
 */
export function useThemeTransition() {
  const updateDom = (val) => {
    if (val) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
    const icon = document.querySelector("link[rel='alternate icon']") || document.querySelector("link[rel='icon']");
    if (icon) {
      icon.href = val ? '/src/public/favicon-light.png?v=2' : '/src/public/favicon-dark.png?v=2';
    }
  };

  /**
   * Toggles theme with expanding glowing water ripple animation from click origin
   * @param {MouseEvent|PointerEvent} [event] Click event to originate circular mask
   */
  const toggleTheme = async (event) => {
    const nextState = !isDark.value;

    // Check View Transitions API support
    if (typeof document !== 'undefined' && 'startViewTransition' in document) {
      let x = window.innerWidth / 2;
      let y = window.innerHeight / 2;

      if (event) {
        const target = event.currentTarget || event.target;
        if (target && typeof target.getBoundingClientRect === 'function') {
          const rect = target.getBoundingClientRect();
          x = rect.left + rect.width / 2;
          y = rect.top + rect.height / 2;
        } else if (typeof event.clientX === 'number') {
          x = event.clientX;
          y = event.clientY;
        }
      }

      const endRadius = Math.hypot(
        Math.max(x, window.innerWidth - x),
        Math.max(y, window.innerHeight - y)
      );

      const transition = document.startViewTransition(() => {
        isDark.value = nextState;
        updateDom(nextState);
      });

      try {
        await transition.ready;
        document.documentElement.animate(
          [
            { clipPath: `circle(0px at ${x}px ${y}px)` },
            { clipPath: `circle(${endRadius * 0.45}px at ${x}px ${y}px)` },
            { clipPath: `circle(${endRadius}px at ${x}px ${y}px)` },
          ],
          {
            duration: 800,
            easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
            pseudoElement: '::view-transition-new(root)',
          }
        );
      } catch (e) {
        isDark.value = nextState;
        updateDom(nextState);
      }
    } else {
      // Fallback for browsers without View Transitions API
      isDark.value = nextState;
      updateDom(nextState);
    }
  };

  onMounted(() => {
    updateDom(isDark.value);
  });

  watch(isDark, (val) => {
    updateDom(val);
  });

  return {
    isDark,
    toggleTheme,
  };
}
